<template>
  <div class="sensorAlarms">
    <Loading v-if="loading" />
    <div class="sensorAlarms__header">
      <div class="chooseDate">
        <div class="date-block">
          <div class="date-inputs">
            <Button
              class="week-nav-button"
              variant="bordered"
              :title="$t('prevWeek') || 'Prev week'"
              @click="prevWeek"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.startTime"
                name="startTime"
                @change="changeDate"
              />
            </div>
            <img
              class="date-img"
              src="@/assets/icons/Hyphen.svg"
              alt="Hyphen"
            />
            <div class="input">
              <Calendar
                type="date"
                format="DD/MM/YYYY"
                :value="dateRange.endTime"
                name="endTime"
                @change="changeDate"
              />
            </div>
            <Button
              class="update-date-button"
              variant="filled"
              style="margin-left: 12px"
              :title="$t('update')"
              :disabled="!isDateCorrect || !isSixMonthValid"
              @click="loadAlarms"
            />
            <Button
              v-if="canGoNextWeek"
              class="week-nav-button"
              variant="bordered"
              style="margin-left: 12px"
              :title="$t('nextWeek') || 'Next week'"
              @click="nextWeek"
            />
          </div>
          <p v-if="!isSixMonthValid" class="date-error">
            {{ $t('errorSixMonths') || 'Range cannot exceed 6 months' }}
          </p>
          <p v-if="!isDateCorrect" class="date-error">
            {{ $t('errorDate') }}
          </p>
        </div>
      </div>
    </div>

    <div v-if="alarmsData.length && isDateCorrect">
      <Table
        :data="alarmsData"
        :columns="columns"
        @comment-sensor-alarm="openCommentModal"
        @comment-sensor-alarm-bulk="openBulkCommentModal"
      />
    </div>
    <div v-else class="sensorAlarms__noData">
      <p>{{ $t('noAlarms') || 'No alarms found for this period' }}</p>
    </div>

    <AddSensorAlarmComment
      :show-modal="showCommentModal"
      :active-alarm="activeAlarm"
      :loading="commentLoading"
      @close-modal="closeCommentModal"
      @save-comment="saveComment"
    />
  </div>
</template>

<script lang="ts">
import { Vue, Options } from 'vue-class-component';
import { mapActions, mapState } from 'vuex';

import { format } from 'date-fns';

import Loading from '@/views/Loading.vue';
import AddSensorAlarmComment from '../AddSensorAlarmComment/AddSensorAlarmComment.vue';

import { columns } from '../SensorsMain/configs';
import { InputFileEvent } from '@/types';
import {
  prevWeekWindow,
  nextWeekWindow,
  canShiftWeekForward
} from '@/utils/shiftDateRange';
import {
  AlarmSensor,
  ResponsesSensor,
  Sensor
} from '@/store/modules/pharmacy/types';

const transformationDate = new Date().setDate(new Date().getDate() - 3);

@Options({
  name: 'SensorAlarms',
  components: {
    Loading,
    AddSensorAlarmComment
  },
  data() {
    return {
      dateRange: {
        startTime: format(new Date(transformationDate), 'dd/MM/yyyy'),
        endTime: format(new Date(), 'dd/MM/yyyy')
      },
      alarmsData: [],
      columns: columns(this.$t),
      loading: false,
      showCommentModal: false,
      commentLoading: false,
      activeAlarm: null,
      activeAlarms: [] as AlarmSensor[]
    };
  },
  computed: {
    ...mapState('account', ['user']),
    ...mapState('pharmacy', ['pharmacy']),
    isDateCorrect() {
      const start = this.parseDate(this.dateRange.startTime);
      const end = this.parseDate(this.dateRange.endTime);

      if (start && end) {
        return end >= start;
      }

      return true;
    },
    isSixMonthValid() {
      const start = this.parseDate(this.dateRange.startTime);
      const end = this.parseDate(this.dateRange.endTime);

      const diffInMs = end - start;
      const sixMonthsInMs = 1000 * 60 * 60 * 24 * 183;

      return diffInMs <= sixMonthsInMs;
    },
    canGoNextWeek() {
      return canShiftWeekForward(this.dateRange);
    }
  },
  created() {
    if (this.user) {
      this.show();
    }
  },
  methods: {
    ...mapActions('pharmacy', [
      'getPharmacy',
      'getAlarmsSensor',
      'sendSensorAlarmComment'
    ]),
    openCommentModal(alarm: AlarmSensor) {
      this.activeAlarm = alarm;
      this.activeAlarms = [alarm];
      this.showCommentModal = true;
    },
    openBulkCommentModal(alarms: AlarmSensor[]) {
      if (!alarms?.length) return;
      this.activeAlarms = alarms;
      this.activeAlarm = alarms[0];
      this.showCommentModal = true;
    },
    closeCommentModal() {
      this.showCommentModal = false;
      this.activeAlarm = null;
      this.activeAlarms = [];
    },
    async saveComment(alarmInfo: string) {
      const alarms = (this.activeAlarms || []).filter(
        (alarm: AlarmSensor & { sensorSn?: string }) =>
          alarm?.sensorSn && alarm?.alarmStartTimestamp
      );

      if (!alarms.length) {
        this.closeCommentModal();
        return;
      }

      this.commentLoading = true;

      for (const alarm of alarms) {
        await this.sendSensorAlarmComment({
          data: {
            sensorSn: (alarm as AlarmSensor & { sensorSn?: string }).sensorSn,
            alarmStartTimestamp: alarm.alarmStartTimestamp,
            alarmInfo
          }
        });
      }

      this.commentLoading = false;
      this.closeCommentModal();

      await this.loadAlarms();
    },
    async show() {
      if (!this.pharmacy) {
        this.loading = true;
        await this.getPharmacy(this.user.pharmacyId);
        this.loading = false;
      }

      await this.loadAlarms();
    },
    parseDate(value: string) {
      const parts = value.split('/');
      return new Date(+parts[2], +parts[1] - 1, +parts[0]).valueOf();
    },
    changeDate(e: InputFileEvent) {
      const { name, value } = e.target;

      this.dateRange = {
        ...this.dateRange,
        [name]: value
      };
    },
    async prevWeek() {
      this.dateRange = prevWeekWindow(this.dateRange);
      await this.loadAlarms();
    },
    async nextWeek() {
      if (!this.canGoNextWeek) return;
      this.dateRange = nextWeekWindow(this.dateRange);
      await this.loadAlarms();
    },
    getSensorAlarmTypeText(alarmType: string, sensorName: string) {
      if (alarmType === 'temp_high') {
        return this.$t('sensorAlarmTempHigh', { name: sensorName });
      }
      if (alarmType === 'temp_low') {
        return this.$t('sensorAlarmTempLow', { name: sensorName });
      }
      return alarmType || '';
    },
    async loadAlarms() {
      if (
        !this.pharmacy?.sensors?.length ||
        !this.isDateCorrect ||
        !this.isSixMonthValid
      ) {
        this.alarmsData = [];
        return;
      }

      this.loading = true;

      const startTime = this.parseDate(this.dateRange.startTime);

      const todayParts = format(new Date(), 'dd/MM/yyyy/HH/mm/ss').split('/');
      const endParts = this.dateRange.endTime.split('/');
      const endTime = new Date(
        +endParts[2],
        +endParts[1] - 1,
        +endParts[0],
        +todayParts[3],
        +todayParts[4],
        +todayParts[5]
      ).valueOf();

      const responses = await Promise.all(
        this.pharmacy.sensors.map((sensor: Sensor) =>
          this.getAlarmsSensor({
            sensorSn: sensor.sn,
            id: sensor.id,
            startTime,
            endTime
          })
        )
      );

      const merged = responses.flatMap((response: ResponsesSensor) => {
        if (!Array.isArray(response.alarmsSensor)) return [];

        const sensor = this.pharmacy.sensors.find(
          (s: Sensor) => s.id === response.id
        );
        const sensorName = sensor?.name || response.id;

        return response.alarmsSensor.map((alarm: AlarmSensor) => ({
          ...alarm,
          id: response.id,
          sensorSn: sensor?.sn,
          nameSensor: sensorName,
          sensorAlarmType: this.getSensorAlarmTypeText(
            alarm.alarmType,
            sensorName
          )
        }));
      });

      merged.sort(
        (a: AlarmSensor, b: AlarmSensor) =>
          b.alarmStartTimestamp - a.alarmStartTimestamp
      );

      this.alarmsData = merged;
      this.loading = false;
    }
  },
  watch: {
    user() {
      if (this.user) {
        this.show();
      }
    }
  }
})
export default class SensorAlarms extends Vue {}
</script>

<style lang="scss" scoped>
.sensorAlarms {
  padding: 0 24px 24px;
  .sensorAlarms__header {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 24px 0;
    .chooseDate {
      display: flex;
      height: 42px;
      .date-block {
        height: 100%;
        .date-inputs {
          display: flex;
          align-items: center;
          .week-nav-button {
            white-space: nowrap;
          }
          .week-nav-button:first-child {
            margin-right: 12px;
          }
          .formkit-outer {
            height: 42px;
            margin-bottom: 0 !important;
            .formkit-wrapper {
              height: 42px;
              .formkit-inner {
                height: 42px;
              }
            }
          }
          .input {
            position: relative;
            z-index: 2;
            margin-bottom: 0 !important;
          }
          .date-img {
            margin: 0 8px;
          }
        }
        .date-error {
          color: $red;
          font-size: $fourteenSize;
          margin-top: 10px;
        }
      }
    }
  }
  .sensorAlarms__noData {
    margin-top: 24px;
    p {
      color: $mid-grey;
      font-size: $fourteenSize;
    }
  }
}
</style>
